/**
 * The Photoreal render window's preview: its settings shown, roughly, in the
 * live 3D view — so a slider's effect can be judged without a render.
 *
 * The browser draws with its own look (renderSettings.ts) and Blender with
 * its own, so the photoreal values cannot simply be copied across. Instead
 * each photoreal setting NUDGES the matching view setting by how far it has
 * moved from its factory value, and each change shows its direction and
 * rough size. The nudges start from the SHIPPED look (lookPreviewBase), never
 * from whatever this browser's own view settings are: a render look defines
 * the picture, as it does for the render (render_room.py lights from the
 * settings alone), so a browser on the "Classic" preset or with sliders moved
 * sees the same preview as a fresh one. Only the browser's look-independent
 * settings (logo, quality, wood and texture recipes) come along. What the
 * browser cannot draw at all is listed in NOT_PREVIEWED and said in the
 * window.
 */

import { DEFAULT_RENDER_SETTINGS, LOOK_INDEPENDENT_KEYS, RenderSettings } from './renderSettings';
import { ContrastLook, FACTORY_PHOTO_RENDER, PhotoRenderSettings } from './photoRender';
import { setTexture } from './textureSets';

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** AgX's contrast looks as a multiplier on the view's contrast (Base = as is). */
const CONTRAST: Record<ContrastLook, number> = {
  'Very Low': 0.8, Low: 0.88, 'Medium Low': 0.94, Base: 1, 'Medium High': 1.07, High: 1.15, 'Very High': 1.25, Punchy: 1.1,
};

/** The photoreal settings the 3D view cannot show (the window lists them when the preview is on). */
export const NOT_PREVIEWED = ['specular', 'clear coat', 'depth of field and bokeh', 'colour fringing', 'the HDRI picture itself', 'the view outside', 'the yard (lawn, driveway, fence, trees)', 'photographic textures other than the floor, doors and framing', 'a fisheye lens', 'the 360', 'samples and output size'] as const;

/**
 * What a look's preview starts from: the shipped look, with only this
 * browser's look-independent settings kept (the logo, the quality, the wood
 * and texture recipes — LOOK_INDEPENDENT_KEYS). Nothing else of the browser's
 * own view settings reaches a look's preview: a render look defines the
 * picture, in the Look Lab and in the customer's designer alike, whatever
 * presets or sliders a browser has touched (measured 2026-10-29: the same
 * room, the same look, fine in a fresh browser and blown out in one carrying
 * old settings).
 */
export function lookPreviewBase(view: RenderSettings): RenderSettings {
  const base: RenderSettings = { ...DEFAULT_RENDER_SETTINGS };
  for (const key of LOOK_INDEPENDENT_KEYS) (base as unknown as Record<string, unknown>)[key] = view[key];
  return base;
}

/** The shipped look with the photoreal settings' changes laid over it (this browser's own view settings aside: lookPreviewBase). */
export function photoPreviewSettings(browserView: RenderSettings, photo: PhotoRenderSettings): RenderSettings {
  const F = FACTORY_PHOTO_RENDER;
  const view = lookPreviewBase(browserView);
  const out: RenderSettings = { ...view };

  // Finishes: the boards' roughness (the browser draws Wall&CeilingBoard and
  // SlatWall with one board material), the floor's.
  const boardShift = (photo.materials.wcb.roughness - F.materials.wcb.roughness + photo.materials.slatwall.roughness - F.materials.slatwall.roughness) / 2;
  out.boardRoughness = clamp(view.boardRoughness + boardShift, 0, 1);
  out.floorRoughness = clamp(view.floorRoughness + (photo.materials.floor.roughness - F.materials.floor.roughness), 0, 1);
  // The joints: their visibility IS the view's seam strength (0 hides them in both).
  if (photo.materials.wcb.joints !== null) out.seamStrength = clamp(photo.materials.wcb.joints, 0, 1);

  // Photographic textures: the floor's, the doors' wood and the framing's sets
  // laid in the view at their real size (textureSets.ts), as the render lays them.
  const t = photo.materials.textures;
  if (t.floor) out.floorTexture = setTexture(t.floor.id, t.floor.scale, t.floor.rotation);
  if (t.wood) { out.doorTexture = setTexture(t.wood.id, t.wood.scale, t.wood.rotation); out.doorTextureTint = 0; }
  if (t.lumber) { out.lumberTexture = setTexture(t.lumber.id, t.lumber.scale, t.lumber.rotation); out.lumberTextureTint = 0; }

  // Light: the sun as the view's key light, the sky or HDRI as its environment, the lamps.
  if (photo.light.sun === 'off' || (photo.light.sun === 'auto' && photo.light.world === 'hdri')) {
    out.keyIntensity = 0;
  } else {
    out.keyIntensity = clamp(view.keyIntensity * (photo.light.sunStrength / F.light.sunStrength), 0, 1);
    out.keyElevation = clamp(view.keyElevation + (photo.light.sunElevation - F.light.sunElevation), -10, 85);
  }
  const outside = photo.light.world === 'hdri' ? photo.light.hdriStrength : photo.light.skyStrength / F.light.skyStrength;
  out.envIntensity = clamp(view.envIntensity * outside, 0, 2);
  out.interiorIntensity = clamp(view.interiorIntensity * photo.light.lampScale, 0, 1.5);
  // The lamps themselves, outright: an even array of the look's fixture at its spacing, its colour.
  out.interiorLayout = 'array';
  out.interiorFixtureStyle = photo.light.lamps.fixture;
  out.interiorSpacing = photo.light.lamps.spacingFt;
  out.interiorWarmth = photo.light.lamps.warmth;
  out.interiorFixtures = photo.light.lamps.fixtures;

  // Effects.
  out.exposure = clamp(view.exposure * Math.pow(2, photo.effects.exposure), 0.3, 2.5);
  if (photo.effects.view === 'Filmic') out.toneMapping = 'aces';
  else if (photo.effects.view === 'Standard') out.toneMapping = 'linear';
  out.contrast = clamp(view.contrast * (photo.effects.view === 'AgX' ? CONTRAST[photo.effects.contrast] : 1), 0.7, 1.5);
  out.saturation = clamp(view.saturation * photo.effects.saturation * (photo.effects.view === 'AgX' && photo.effects.contrast === 'Punchy' ? 1.1 : 1), 0, 2);
  if (photo.effects.contact <= 0) out.ao = false;
  else out.aoIntensity = clamp(view.aoIntensity * photo.effects.contact, 0, 2);
  if (photo.effects.bloom > 0) {
    out.bloom = true;
    out.bloomStrength = clamp((view.bloom ? view.bloomStrength : 0) + photo.effects.bloom, 0, 2);
  }
  out.vignette = clamp(view.vignette + photo.effects.vignette, 0, 1);
  // Film grain on top of the view's trace of it (the render's 1 is strong; the view's scale tops out at 0.15), and the lens's softness as is.
  out.grain = clamp(view.grain + photo.effects.grain * 0.06, 0, 0.15);
  out.softness = clamp(photo.effects.softness, 0, 1);
  // The meter (exposureMeter.ts): on in the view exactly when it is on in the render.
  out.autoExposure = photo.effects.autoExposure;
  return out;
}
